import React, { useEffect, useRef } from 'react'
import { isNil } from 'lodash'
import Lottie from 'lottie-react'

import './CogitationDog.scss'
import BaltoWinking from '@/assets/lotties/BaltoWinking.json'

// One continuous loader. The orb plays its choreography (2×2 squares → 45° diamond →
// circleize one-by-one) in the gradient — no resting ball. As the circleized cells
// converge and fade, the Balto winking wolf (the BaltoWinking Lottie, recoloured to
// brand purple) draws itself in, plays its wink + nod, then draws out as the cells
// re-spread. Both seams overlap so it never sits empty.

// ─── easing ──────────────────────────────────────────────────────────────────
function clamp01(x) {
  return Math.max(0, Math.min(1, x))
}
function norm(t, s, e) {
  return clamp01((t - s) / (e - s))
}
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}
function easeOut3(t) {
  return 1 - (1 - t) ** 3
}
function easeIn3(t) {
  return t ** 3
}
function easeOutBack(t, k = 1.7) {
  const c = k + 1
  return 1 + c * (t - 1) ** 3 + k * (t - 1) ** 2
}
function lerp(a, b, t) {
  return a + (b - a) * t
}

// ─── recolour the Lottie to brand colours (done once at module load) ──────────
const BRAND_FILL = [0.424, 0.298, 0.941] // purple wolf body
const BRAND_STROKE = [0.831, 0.361, 0.784] // pink line-draw accent
function recolourWolf(data) {
  const clone = JSON.parse(JSON.stringify(data))
  const walk = (node) => {
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (node && typeof node === 'object') {
      if ((node.ty === 'fl' || node.ty === 'st') && node.c && Array.isArray(node.c.k)) {
        const target = node.ty === 'fl' ? BRAND_FILL : BRAND_STROKE
        ;[node.c.k[0], node.c.k[1], node.c.k[2]] = target
      }
      Object.values(node).forEach(walk)
    }
  }
  walk(clone)
  return clone
}
const BRAND_WOLF = recolourWolf(BaltoWinking)

// ─── palette ────────────────────────────────────────────────────────────────
const COAT_STOPS = [
  'oklch(92% 0.04 5)',
  'oklch(84% 0.12 342)',
  'oklch(76% 0.19 318)',
  'oklch(68% 0.23 300)',
  'oklch(56% 0.27 282)',
  'oklch(44% 0.25 265)',
  'oklch(36% 0.19 250)',
]

// Whole loop runs in one cycle; ~half orb, ~half wolf, overlapping at the seams.
const CYCLE_MS = 7500

// ─── geometry ─────────────────────────────────────────────────────────────────
// Orb cell grid: 4 squares of CELL, GAP apart → span 2*CELL+GAP = 60 = ball ⌀.
const CELL = 24
const GAP = 12
const HALF = (CELL + GAP) / 2

// Orb circleize choreography (ported from CogitationOrb.js).
const CIRCLE_ORDER = [0, 1, 3, 2] // after the 45° turn: top → right → bottom → left
const CW_START = 0.34
const CW = 0.04
const CGAP = 0.006
const TARGETS = [
  { x: -HALF, y: -HALF }, // 0 TL
  { x: HALF, y: -HALF }, // 1 TR
  { x: -HALF, y: HALF }, // 2 BL
  { x: HALF, y: HALF }, // 3 BR
]
const CIRCLE_WIN = {}
const SHRINK_TRIG = {}
CIRCLE_ORDER.forEach((cellIdx, order) => {
  const start = CW_START + order * (CW + CGAP)
  CIRCLE_WIN[cellIdx] = { start, end: start + CW }
})
CIRCLE_ORDER.forEach((cellIdx, order) => {
  const nextIdx = CIRCLE_ORDER[order + 1]
  SHRINK_TRIG[cellIdx] = !isNil(nextIdx) ? CIRCLE_WIN[nextIdx].start : CIRCLE_WIN[cellIdx].end
})

let gradSeq = 0

function svgMarkup(gid) {
  const stops = COAT_STOPS.map(
    (color, index) => `<stop offset="${index / (COAT_STOPS.length - 1)}" stop-color="${color}"/>`
  ).join('')
  const coat = `fill="url(#${gid})"`
  const rects = [0, 1, 2, 3]
    .map((index) => `<rect class="cdg-c${index}" ${coat} width="${CELL}" height="${CELL}"/>`)
    .join('')
  return `
    <defs>
      <linearGradient id="${gid}" gradientUnits="userSpaceOnUse" x1="20" y1="22" x2="80" y2="82">
        ${stops}
      </linearGradient>
    </defs>
    <g class="cdg-root">
      <g class="cdg-cells" opacity="0">${rects}</g>
    </g>
  `
}

// ─── OrbAnimator: the gradient orb choreography (no resting ball) ─────────────
class OrbAnimator {
  constructor(stageEl) {
    const gid = `cdg-grad-${(gradSeq += 1)}`
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 100 92')
    svg.setAttribute('class', 'cogitation-dog__svg')
    svg.setAttribute('aria-hidden', 'true')
    svg.innerHTML = svgMarkup(gid)
    stageEl.appendChild(svg)

    const pick = (sel) => svg.querySelector(sel)
    this.svg = svg
    this.grad = pick(`#${gid}`)
    this.cellsG = pick('.cdg-cells')
    this.cells = [0, 1, 2, 3].map((index) => pick(`.cdg-c${index}`))
    this._lastGradAngle = null
  }

  setGrad(angle) {
    const rounded = Math.round(angle)
    if (rounded === this._lastGradAngle) {
      return
    }
    this._lastGradAngle = rounded
    this.grad.setAttribute('gradientTransform', `rotate(${rounded} 50 52)`)
  }

  render(t) {
    this.cellsG.style.opacity = 1
    const spreadP = easeOut3(norm(t, 0.04, 0.11))
    const squareP = easeInOut(norm(t, 0.04, 0.11))
    const rotateP = easeOutBack(norm(t, 0.11, 0.27), 1.7)
    const collapseP = easeIn3(norm(t, 0.6, 0.68)) // cells converge to centre and fade out

    this.cells.forEach((cell, index) => {
      const tgt = TARGETS[index]
      const tx = tgt.x * (spreadP - collapseP)
      const ty = tgt.y * (spreadP - collapseP)

      const cw = CIRCLE_WIN[index]
      const circleP = easeInOut(norm(t, cw.start, cw.end))
      const radiusPct = circleP > 0 ? lerp(0, 50, circleP) : lerp(50, 0, squareP)

      const popT = norm(t, cw.start, cw.end)
      const shrinkAt = SHRINK_TRIG[index]
      const shrinkP = easeInOut(norm(t, shrinkAt, shrinkAt + 0.012))
      const cellScale = t <= shrinkAt ? 1 + 0.25 * Math.sin(Math.PI * popT) : lerp(1, 0.75, shrinkP)
      const opacity = Math.min(spreadP, 1 - collapseP) * (1 - shrinkP * 0.6)

      const cx = 50 + tx
      const cy = 52 + ty
      cell.setAttribute('x', cx - CELL / 2)
      cell.setAttribute('y', cy - CELL / 2)
      cell.setAttribute('rx', (radiusPct * CELL) / 100)
      cell.setAttribute('ry', (radiusPct * CELL) / 100)
      cell.setAttribute(
        'transform',
        `translate(${cx} ${cy}) scale(${cellScale}) translate(${-cx} ${-cy})`
      )
      cell.style.opacity = opacity
    })

    this.cellsG.setAttribute('transform', `rotate(${rotateP * 45} 50 52)`)
  }
}

// ─── Component ───────────────────────────────────────────────────────────────
/**
 * @import import { CogitationDog } from '@/components/ai/CogitationDog'
 * @param {object} props
 * @param {boolean} [props.resting] Holds the fully drawn wolf instead of looping the animation
 */
export function CogitationDog({ resting = false }) {
  const orbRef = useRef(null)
  const wolfRef = useRef(null) // wraps the <Lottie>, opacity cross-faded
  const lottieRef = useRef(null) // the Lottie AnimationItem

  useEffect(() => {
    const orbEl = orbRef.current
    const wolfEl = wolfRef.current
    if (!orbEl) {
      return () => {}
    }

    const orb = new OrbAnimator(orbEl)
    const mql =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null

    if (resting || mql?.matches) {
      orbEl.style.opacity = 0
      if (wolfEl) {
        wolfEl.style.opacity = 1
      }
      lottieRef.current?.goToAndStop(80, true) // hold the fully-drawn wolf
      return () => {
        orb.svg.remove()
      }
    }

    let startTime = null
    let wolfPlaying = false
    let rafId

    function drawFrame(now) {
      if (isNil(startTime)) {
        startTime = now
      }
      const cp = ((now - startTime) % CYCLE_MS) / CYCLE_MS
      orb.setGrad(135 + cp * 720) // 2 full gradient turns per cycle ⇒ seamless spin
      // The orb runs on a shifted clock: the cells converge to centre and fade out (no
      // resting ball) before the wolf appears, and only re-spread after the wolf has fully
      // faded — so both hand-offs are clean, with no overlap on either side.
      orb.render((((cp - 0.9 + 1) % 1) / 0.55) * 0.72)

      // Wolf draws in only after the orb cells are gone, holds, then fades out on its own.
      let wolf
      if (cp < 0.42) {
        wolf = 0
      } else if (cp < 0.56) {
        wolf = easeInOut(norm(cp, 0.42, 0.56))
      } else if (cp < 0.8) {
        wolf = 1
      } else if (cp < 0.93) {
        wolf = 1 - easeInOut(norm(cp, 0.8, 0.93))
      } else {
        wolf = 0
      }
      if (wolfEl) {
        wolfEl.style.opacity = wolf
      }

      // Replay the wolf's draw-in fresh each time it appears.
      if (wolf > 0.01 && !wolfPlaying) {
        wolfPlaying = true
        lottieRef.current?.goToAndPlay(0, true)
      } else if (wolf <= 0.01 && wolfPlaying) {
        wolfPlaying = false
      }

      rafId = requestAnimationFrame(drawFrame)
    }

    const handleMotionChange = (event) => {
      if (event.matches) {
        cancelAnimationFrame(rafId)
        orbEl.style.opacity = 0
        if (wolfEl) {
          wolfEl.style.opacity = 1
        }
        lottieRef.current?.goToAndStop(80, true)
      }
    }
    mql?.addEventListener('change', handleMotionChange)

    rafId = requestAnimationFrame(drawFrame)
    return () => {
      cancelAnimationFrame(rafId)
      mql?.removeEventListener('change', handleMotionChange)
      orb.svg.remove()
    }
  }, [resting])

  return (
    <div className="cogitation-dog" role="img" aria-label="Balto assistant loading">
      <div ref={orbRef} className="cogitation-dog__orb" />
      <div ref={wolfRef} className="cogitation-dog__wolf">
        <Lottie
          lottieRef={lottieRef}
          animationData={BRAND_WOLF}
          loop
          autoplay={false}
          rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
        />
      </div>
    </div>
  )
}
